import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { ProjectService } from '@/services/ProjectService';
import ProjectHeader from '@/components/header';
import { motion, AnimatePresence } from 'framer-motion';
import { Save } from 'lucide-react';
import FileUploadCard from '@/components/FileUploadCard';
import { useCollectionData } from '@/hooks/useCollectionData';
import CollectionNavigation from '@/components/CollectionNavigation';
import { AssetsConfig } from '@/services/AssetsConfig';

const Section = ({ title, icon, children }) => {
    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-white rounded-lg p-6 shadow-sm border-l-4 border-blue-500"
        >
            <div className="flex items-center gap-3 mb-4">
                <div className="text-3xl p-2 bg-blue-50 rounded-full w-12 h-12 flex items-center justify-center">
                    {icon}
                </div>
                <h3 className="text-lg font-semibold">{title}</h3>
            </div>
            <AnimatePresence>
                <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                >
                    {children}
                </motion.div>
            </AnimatePresence>
        </motion.div>
    );
};


const QuestionRenderer = ({ question, formData, updateFormData, projectId, fileItems, setFileItems }) => {
    const isVisible = !question.conditions || question.conditions.every(condition => {
        const conditionQuestion = condition.questionId;
        const formValue = formData[conditionQuestion]?.toString?.().toLowerCase?.();
        const conditionValue = condition.value.toString().toLowerCase();
        
        switch (condition.operator) {
            case 'not_equals':
                return formValue !== conditionValue;
            case 'contains':
                return formValue?.includes(conditionValue);
            case 'not_contains':
                return !formValue?.includes(conditionValue);
            default: // equals
                return formValue === conditionValue;
        }
    });
    
    if (!isVisible || question.visible === false) return null;

    const QuestionLabel = () => (
        <div className="space-y-2">
            <div className="flex items-center gap-2 mb-2">
                {question.icon && <span className="text-2xl">{question.icon}</span>}
                <Label>{question.question}</Label>
            </div>
            {question.helpText && question.type !== 'text' && (
                <p className="text-sm text-gray-500">{question.helpText}</p>
            )}
            {question.helpImage && (
                <div className="mt-2 border rounded-lg overflow-hidden inline-block">
                    <img 
                        src={question.helpImage} 
                        alt="Help visualization" 
                        style={{
                            width: question.helpImageWidth || 300,
                            height: question.helpImageHeight || 200,
                            objectFit: 'contain'
                        }}
                    />
                </div>
            )}
        </div>
    );

    switch (question.type) {
        case 'radio':
            return (
                <RadioGroup
                    value={formData[question.id]}
                    onValueChange={value => updateFormData(question.id, value)}
                    className="space-y-2"
                >
                    <QuestionLabel />
                    {question.options.map(option => (
                        <motion.div
                            key={option}
                            whileHover={{ scale: 1.02 }}
                            className="flex items-center space-x-2 p-2 rounded-lg hover:bg-gray-50"
                        >
                            <RadioGroupItem 
                                value={typeof option === 'object' ? option.value : option.toLowerCase().replace(/\s+/g, '_')} 
                                id={option}
                            />
                            <Label htmlFor={option}>
                                {typeof option === 'object' ? option.label : option}
                            </Label>
                        </motion.div>
                    ))}
                </RadioGroup>
            );

        case 'dropdown':
            return (
                <div className="space-y-2">
                    <QuestionLabel />
                    <Select 
                        value={formData[question.id]} 
                        onValueChange={value => updateFormData(question.id, value)}
                    >
                        <SelectTrigger>
                            <SelectValue placeholder={question.question} />
                        </SelectTrigger>
                        <SelectContent>
                            {question.options.map(option => (
                                <SelectItem 
                                    key={option}
                                    value={typeof option === 'object' ? option.value : option.toLowerCase().replace(/\s+/g, '_')}
                                >
                                    {typeof option === 'object' ? option.label : option}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            );

        case 'text':
            return (
                <div className="space-y-2">
                    <QuestionLabel />
                    {question.id.includes('notes') ? (
                        <Textarea
                            placeholder={question.helpText}
                            value={formData[question.id] || ''}
                            onChange={e => updateFormData(question.id, e.target.value)}
                            className="min-h-[100px]"
                        />
                    ) : (
                        <Input
                            placeholder={question.helpText}
                            value={formData[question.id] || ''}
                            onChange={e => updateFormData(question.id, e.target.value)}
                        />
                    )}
                </div>
            );

        case 'multiple_select':
            return (
                <div className="space-y-2">
                    <QuestionLabel />
                    <div className="grid grid-cols-2 gap-4">
                        {question.options.map(option => (
                            <motion.div
                                key={option}
                                whileHover={{ scale: 1.02 }}
                                className="flex items-center space-x-2 p-2 rounded-lg hover:bg-gray-50"
                            >
                                <Checkbox
                                    id={option}
                                    checked={(formData[question.id] || []).includes(
                                        typeof option === 'object' ? option.value : option
                                    )}
                                    onCheckedChange={checked => {
                                        const value = typeof option === 'object' ? option.value : option;
                                        const currentValues = formData[question.id] || [];
                                        const newValues = checked
                                            ? [...currentValues, value]
                                            : currentValues.filter(item => item !== value);
                                        updateFormData(question.id, newValues);
                                    }}
                                />
                                <Label htmlFor={option}>
                                    {typeof option === 'object' ? option.label : option}
                                </Label>
                            </motion.div>
                        ))}
                    </div>
                </div>
            );

        case 'file_upload':
            return (
                <div className="space-y-2">
                    <QuestionLabel />
                    <FileUploadCard
                        title={question.question}
                        projectId={projectId}
                        onFilesChange={files => {
                            setFileItems(files);
                        }}
                        initialFiles={fileItems}
                        cardClassName="h-full"
                        defaultCollapsed={true}
                        config={question.fileConfig}
                    />
                </div>
            );

        case 'picture_select':
            return (
                <div className="space-y-2">
                    <QuestionLabel />
                    <RadioGroup
                        value={formData[question.id]}
                        onValueChange={value => updateFormData(question.id, value)}
                        className="grid grid-cols-2 gap-4"
                    >
                        {question.options.map(option => (
                            <motion.div
                                key={option.value}
                                whileHover={{ scale: 1.02 }}
                                className="relative"
                            >
                                <RadioGroupItem
                                    value={option.value}
                                    id={option.value}
                                    className="absolute top-2 left-2 z-10"
                                />
                                <Label
                                    htmlFor={option.value}
                                    className="block cursor-pointer"
                                >
                                    <div className="overflow-hidden rounded-lg border-2 hover:border-blue-500 transition-colors">
                                        <div className="aspect-video flex items-center justify-center bg-gray-50">
                                            <img
                                                src={option.imageUrl}
                                                alt={option.label}
                                                className="max-w-full max-h-full object-contain"
                                            />
                                        </div>
                                        <div className="p-2 bg-white">
                                            <p className="font-medium">{option.label}</p>
                                        </div>
                                    </div>
                                </Label>
                            </motion.div>
                        ))}
                    </RadioGroup>
                </div>
            );

        default:
            return null;
    }
};

const AdditionalInfo = ({ flowId, previewMode = false, previewData = null, previewType = null }) => {
    const navigate = useNavigate();
    const { projectId } = useParams();
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);
    const [lastSaved, setLastSaved] = useState(null);
    const [fileItems, setFileItems] = useState([]);
    const [projectData, setProjectData] = useState(null);
    const [formData, setFormData] = useState({});
    const { data: collectionData, iscollectionLoading } = useCollectionData(projectData?.collectionId);
    const [questions, setQuestions] = useState(null);
    const [projectType, setProjectType] = useState(null);

    

    // Load saved data
    useEffect(() => {
        const loadProjectData = async () => {
            if (previewMode && previewData) {
                setIsLoading(false);
                console.log('Preview data:', previewData);
                setQuestions(previewData);
                setProjectType(previewType);
                return;
            }
            try {
                setIsLoading(true);
                const project = await ProjectService.getProjectById(projectId);
                setProjectData(project);
                const Type = project.projectType;
                setProjectType(Type);
                
                // console.log('projectType:', Type);
                await loadQuestions(Type);
                if (project.kitchenDetails) {
                    setFormData(project.kitchenDetails);
                }
                if (project.fileItems) {
                    const parsedFileItems = project.fileItems
                        ? typeof project.fileItems === 'string'
                            ? JSON.parse(project.fileItems)
                            : project.fileItems
                        : [];
                    setFileItems(parsedFileItems);
                }
            } catch (error) {
                console.error('Error loading project data:', error);
            } finally {
                setIsLoading(false);
            }
        };

        loadProjectData();
    }, [projectId, previewMode, previewData]);

    const loadQuestions = async (type = "kitchen") => {
        try {
            const Data = await AssetsConfig.getQuestions({flow: type});
            console.log('Questions:', Data);
            setQuestions(Data);
        } catch (error) {
            console.error('Error loading questions:', error);
        }
    };
           

    // Handle form changes
    const updateFormData = (field, value) => {
        setFormData(prev => {
            const newData = { ...prev, [field]: value };
            setHasChanges(true);
            return newData;
        });
    };

    // Auto-save timer
    useEffect(() => {
        let autoSaveTimer;

        if (hasChanges) {
            autoSaveTimer = setTimeout(async () => {
                await handleSave();
            }, 10000);
        }

        return () => {
            if (autoSaveTimer) {
                clearTimeout(autoSaveTimer);
            }
        };
    }, [hasChanges, formData]);

    const handleSave = async () => {
        try {
            setIsSaving(true);
            await ProjectService.updateProject(projectId, {
                kitchenDetails: formData,
            });
            setHasChanges(false);
            setLastSaved(new Date());
        } catch (error) {
            console.error('Error saving project:', error);
        } finally {
            setIsSaving(false);
        }
    };

    const handleComplete = async () => {
        try {
            setIsSaving(true);
            await handleSave();
            await ProjectService.addCompletedFlow(projectId, flowId);
            navigate(`/project/${projectId}`);
        } catch (error) {
            console.error('Error completing section:', error);
        } finally {
            setIsSaving(false);
        }
    };

    const renderContent = () => (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="container mx-auto max-w-3xl px-1 sm:px-4"
        >
            <Card className="mb-6">
                <CardHeader className="text-center">
                    <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} transition={{ duration: 0.5 }}>
                        <CardTitle className="text-3xl bg-gradient-to-r from-blue-600 to-blue-400 bg-clip-text text-transparent">
                            {questions.header ? questions.header : `Design Your Dream ${projectType.charAt(0).toUpperCase() + projectType.slice(1)}`}
                        </CardTitle>
                        <span className="ml-2 text-3xl">{questions.icon? questions.icon : ''}</span>
                        <p className="text-gray-600 mt-2">
                            Help us understand your project better to create the perfect space for you
                        </p>
                    </motion.div>
                </CardHeader>

                <CardContent className="space-y-6">
                    {questions?.categories.map(category => (
                        <Section key={category.id} title={category.name} icon={category.icon}>
                            {category.questions.map(question => (
                                <div key={question.id} className="mb-8">
                                    <QuestionRenderer
                                        key={question.id}
                                        question={question}
                                        formData={formData}
                                        updateFormData={updateFormData}
                                        projectId={projectId}
                                        fileItems={fileItems}
                                        setFileItems={setFileItems}
                                    />
                                </div>
                            ))}
                        </Section>
                    ))}
                </CardContent>
            </Card>
        </motion.div>
    );

    return (
        <div className="min-h-screen bg-gray-50 pb-24">
            {!previewMode && (
                <>
            <ProjectHeader 
                showBackButton={true}
                collectionData={collectionData}
                projectName={projectData?.name}
            />
            </>
            )}
            <main className="pt-20 px-2 sm:px-4">
                {projectData?.collectionId && !previewMode && (
                    <CollectionNavigation 
                        collectionData={collectionData}
                        isLoading={iscollectionLoading}
                        currentProjectId={projectId}
                    />
                )}
                {isLoading || !questions ? (
                    <div className="min-h-screen bg-gray-100 flex items-center justify-center">
                        <div className="space-y-4 text-center">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
                            <p className="text-gray-600">Loading your project details...</p>
                        </div>
                    </div>
                ) : (
                    renderContent()
                )}
            </main>

            {!previewMode && (
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 flex justify-between items-center shadow-lg"
            >
                <div className="flex items-center gap-4">
                    <Button
                        variant="outline"
                        onClick={handleSave}
                        disabled={isSaving || !hasChanges}
                        className="flex items-center gap-2"
                    >
                        <Save className="w-4 h-4" />
                        {isSaving ? 'Saving...' : 'Save Progress'}
                    </Button>
                    {lastSaved && (
                        <span className="text-sm text-gray-500">Last saved: {lastSaved.toLocaleTimeString()}</span>
                    )}
                    {hasChanges && <span className="text-sm text-yellow-600">• Unsaved changes</span>}
                </div>
                <Button onClick={handleComplete} className="min-w-[160px]" disabled={isSaving}>
                    {isSaving ? 'Saving...' : 'Complete Section'}
                </Button>
            </motion.div>
            )}
        </div>
    );
};

export default AdditionalInfo;
